export type MetricsPeriod = 7 | 30 | 90;
export type MetricsDay = { date: string; visits: number; pageViews: number; activeSeconds: number };
export type TrafficMetrics = {
  visits: number; pageViews: number; activeSeconds: number; averageActiveSeconds: number | null;
  sampledVisits: number; byDay: MetricsDay[];
};
export type CommerceMetrics = {
  paidOrders: number; completedOrders: number; grossItemCents: number; buyerPaidCents: number;
  shippingCents: number; protectionCents: number; sellerFeeCents: number; shippingHandlingCents: number;
  refundedOrders: number; refundedBuyerCents: number;
};
export type OwnerMetrics = { available: true; periodDays: MetricsPeriod; traffic: TrafficMetrics; commerce: CommerceMetrics; note: string };
export type MetricsUnavailable = { available: false; note: string };
export type ListingMetric = { listingId: string; viewCount: number; saveCount: number };
export type SellerMetrics = { available: true; items: ListingMetric[]; note: string };

/** Presentation gate only; private endpoints independently enforce the server allowlist. */
export function managementGate(user: User | null, config: PublicConfig | null): "login" | "verify" | "denied" | "unavailable" | "allowed" {
  if (!user) return "login";
  if (user.isAdmin !== true) return "denied";
  if (user.emailVerified !== true) return "verify";
  if (config?.configured !== true) return "unavailable";
  return "allowed";
}

const invalid = () => new Error("Le statistiche non hanno restituito dati verificabili. Riprova.");
const record = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
const counter = (value: unknown): number => {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) throw invalid();
  return value;
};
const note = (value: unknown) => typeof value === "string" ? value.slice(0, 1200) : "";

export function metricsPeriod(value: unknown): MetricsPeriod {
  if (value !== 7 && value !== 30 && value !== 90) throw invalid();
  return value;
}

export function normalizeOwnerMetrics(value: unknown, expectedPeriod?: MetricsPeriod): OwnerMetrics | MetricsUnavailable {
  const result = record(value);
  if (!result || typeof result.available !== "boolean") throw invalid();
  if (result.available === false) return { available: false, note: note(result.note) };
  const periodDays = metricsPeriod(result.periodDays);
  if (expectedPeriod !== undefined && periodDays !== expectedPeriod) throw invalid();
  const traffic = record(result.traffic);
  const commerce = record(result.commerce);
  if (!traffic || !commerce || !Array.isArray(traffic.byDay) || traffic.byDay.length > periodDays) throw invalid();
  const dates = new Set<string>();
  const byDay = traffic.byDay.map((value): MetricsDay => {
    const day = record(value);
    const date = day?.date;
    if (!day || typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date) || dates.has(date)) throw invalid();
    const parsed = new Date(`${date}T00:00:00Z`);
    if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) throw invalid();
    dates.add(date);
    return { date, visits: counter(day.visits), pageViews: counter(day.pageViews), activeSeconds: counter(day.activeSeconds) };
  }).sort((left, right) => left.date.localeCompare(right.date));
  const visits = counter(traffic.visits);
  const sampledVisits = counter(traffic.sampledVisits);
  const activeSeconds = counter(traffic.activeSeconds);
  if (sampledVisits > visits) throw invalid();
  const average = traffic.averageActiveSeconds;
  if (average !== null && (typeof average !== "number" || !Number.isFinite(average) || average < 0 || average > Number.MAX_SAFE_INTEGER)) throw invalid();
  if (visits > 0 && average === null) throw invalid();
  return {
    available: true, periodDays, note: note(result.note),
    traffic: {
      visits, pageViews: counter(traffic.pageViews), activeSeconds,
      averageActiveSeconds: visits > 0 ? Math.round(activeSeconds / visits * 10) / 10 : null, sampledVisits, byDay,
    },
    commerce: {
      paidOrders: counter(commerce.paidOrders), completedOrders: counter(commerce.completedOrders),
      grossItemCents: counter(commerce.grossItemCents), buyerPaidCents: counter(commerce.buyerPaidCents),
      shippingCents: counter(commerce.shippingCents), protectionCents: counter(commerce.protectionCents),
      sellerFeeCents: counter(commerce.sellerFeeCents), shippingHandlingCents: counter(commerce.shippingHandlingCents),
      refundedOrders: counter(commerce.refundedOrders), refundedBuyerCents: counter(commerce.refundedBuyerCents),
    },
  };
}

export function normalizeSellerMetrics(value: unknown): SellerMetrics | MetricsUnavailable {
  const result = record(value);
  if (!result || typeof result.available !== "boolean") throw invalid();
  if (result.available === false) return { available: false, note: note(result.note) };
  if (!Array.isArray(result.items) || result.items.length > 10000) throw invalid();
  const ids = new Set<string>();
  const items = result.items.map((value): ListingMetric => {
    const item = record(value);
    const id = item?.listingId;
    if (!item || typeof id !== "string" || !/^[a-f0-9]{32}$/.test(id) || ids.has(id)) throw invalid();
    ids.add(id);
    return { listingId: id, viewCount: counter(item.viewCount), saveCount: counter(item.saveCount) };
  });
  return { available: true, items, note: note(result.note) };
}

export function metricsMoney(cents: number) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(counter(cents) / 100);
}

export function metricsCount(value: number) {
  return new Intl.NumberFormat("it-IT").format(counter(value));
}

export function metricsDuration(seconds: number | null) {
  if (seconds === null) return "—";
  if (!Number.isFinite(seconds) || seconds < 0 || seconds > Number.MAX_SAFE_INTEGER) throw invalid();
  const whole = Math.round(seconds);
  if (whole < 60) return `${whole} s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  if (minutes < 60) return `${minutes} min${rest ? ` ${rest} s` : ""}`;
  return `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
}

export function metricsDayLabel(value: string) {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "UTC" });
}
import type { PublicConfig } from "../lib/api";
import type { User } from "../types";

